import React from "react";
import { Helmet } from "react-helmet";

export default function AfterAbortionCare() {
  return (
    <div className="font-nunito bg-gradient-to-b from-purple-50/30 via-blue-50/20 to-pink-50/20">
      <Helmet>
        <link rel="icon" href="favicon.ico" />
        <title>After Abortion Care | Pathway Resource Center</title>
        <meta
          name="description"
          content="Compassionate support and healing resources after abortion. Free, confidential care in a safe, judgment-free environment."
        />
      </Helmet>

      {/* Hero Section - Gentle & Welcoming */}
      <section className="relative overflow-hidden bg-gradient-to-br from-purple-100/40 via-blue-50/30 to-pink-50/40 py-16 md:py-24">
        {/* Soft Decorative Elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute left-0 top-0 h-96 w-96 rounded-full bg-purple-200 blur-3xl"></div>
          <div className="absolute right-0 bottom-0 h-80 w-80 rounded-full bg-blue-200 blur-3xl"></div>
          <div className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-pink-200 blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-4xl px-6 text-center">
          {/* Gentle Heart Icon */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 animate-pulse rounded-full bg-gradient-to-br from-pink-300 to-purple-300 opacity-30 blur-2xl"></div>
              <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-pink-200/50 to-purple-200/50 backdrop-blur-sm shadow-lg">
                <svg
                  className="h-10 w-10 text-[#457B9D]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>

          <h1 className="mb-6 text-4xl font-semibold leading-tight text-gray-800 md:text-5xl lg:text-6xl">
            You Are Not Alone
          </h1>

          <p className="mx-auto mb-8 max-w-2xl text-xl leading-relaxed text-gray-600 md:text-2xl">
            A safe space for healing, support, and understanding
          </p>

          <div className="mx-auto max-w-3xl rounded-3xl bg-white/60 backdrop-blur-sm p-6 md:p-8 shadow-xl">
            <p className="text-lg leading-relaxed text-gray-700">
              All our services are{" "}
              <span className="font-semibold text-[#457B9D]">completely free</span> and{" "}
              <span className="font-semibold text-[#457B9D]">100% confidential</span>.
              We're here to support you with compassion, grace, and understanding.
            </p>
          </div>
        </div>

        {/* Gentle Wave Divider */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg
            viewBox="0 0 1440 120"
            className="w-full"
            preserveAspectRatio="none"
            style={{ height: '80px' }}
          >
            <path
              d="M0,64 C360,100 720,20 1440,64 L1440,120 L0,120 Z"
              fill="white"
              opacity="0.5"
            />
            <path
              d="M0,80 C360,40 720,100 1440,80 L1440,120 L0,120 Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Main Content Container */}
      <div className="mx-auto max-w-5xl px-6 py-12 md:py-16">

        {/* Healing After Abortion Section */}
        <section className="mb-16">
          <div className="overflow-hidden rounded-3xl bg-white shadow-lg">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-8 md:p-12">
              <div className="mb-6 inline-block rounded-full bg-white/80 px-6 py-2 shadow-sm">
                <span className="text-sm font-semibold uppercase tracking-wide text-[#457B9D]">
                  Emotional Support
                </span>
              </div>

              <h2 className="mb-6 text-3xl font-semibold text-gray-800 md:text-4xl">
                Healing After Abortion
              </h2>

              <div className="space-y-6 text-lg leading-relaxed text-gray-700">
                <p>
                  At Pathway, in partnership with{" "}
                  <span className="font-medium text-[#457B9D]">She Found His Grace</span>,
                  we recognize the complicated blend of physical and emotional needs that
                  arise after pregnancy termination. Our mission is to provide a supportive
                  environment where you can find solace, understanding, and guidance as you
                  navigate through this time.
                </p>

                <p>
                  Are you experiencing a whirlwind of emotions? Whether you're dealing with
                  feelings of anger, shame, guilt, or an overwhelming sense of isolation, we
                  understand the complexities involved. You're not alone. We offer a sanctuary
                  of grace, love, and compassion, tailored to help you embark on a journey of
                  healing and restoration.
                </p>

                <p>
                  Our approach includes online classes designed to ease the processing of grief
                  and emotional turmoil. Our dedicated team is committed to addressing your
                  concerns and providing a safe space for open dialogue and support.
                </p>
              </div>

              {/* CTA Button */}
              <div className="mt-10 flex justify-center">
                <a
                  href="sms:+18707792229"
                  className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-[#457B9D] to-[#5a8dad] px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  <span>Text to Make an Appointment</span>
                  <svg
                    className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </a>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="p-8 md:p-12">
              <h3 className="mb-8 text-2xl font-semibold text-gray-800">
                Frequently Asked Questions
              </h3>

              <div className="space-y-4">
                {/* Physical Side Effects FAQ */}
                <details className="group overflow-hidden rounded-2xl border-2 border-purple-100 bg-gradient-to-br from-white to-purple-50/30 transition-all duration-300 hover:border-purple-200 hover:shadow-md">
                  <summary className="cursor-pointer list-none p-6 font-semibold text-gray-800 transition-colors duration-200 group-open:text-[#457B9D]">
                    <div className="flex items-center justify-between">
                      <span className="text-lg">What are common physical side effects?</span>
                      <svg
                        className="h-6 w-6 text-[#457B9D] transition-transform duration-300 group-open:rotate-180"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </summary>

                  <div className="border-t border-purple-100 bg-white px-6 pb-6 pt-4">
                    <div className="space-y-4 text-gray-700 leading-relaxed">
                      <div className="flex gap-3">
                        <span className="font-semibold text-[#457B9D] flex-shrink-0">Bleeding:</span>
                        <p>Light to moderate bleeding lasting 2-6 weeks. Seek emergency medical help for continued heavy bleeding.</p>
                      </div>
                      <div className="flex gap-3">
                        <span className="font-semibold text-[#457B9D] flex-shrink-0">Discharge:</span>
                        <p>Monitor for changes in color and odor. Consult a doctor if discharge persists or intensifies.</p>
                      </div>
                      <div className="flex gap-3">
                        <span className="font-semibold text-[#457B9D] flex-shrink-0">Pain/Cramping:</span>
                        <p>Normal pressure in the lower abdomen. Persistent or increasing pain warrants medical evaluation.</p>
                      </div>
                      <div className="flex gap-3">
                        <span className="font-semibold text-[#457B9D] flex-shrink-0">Fatigue:</span>
                        <p>Normal tiredness; prioritize rest, hydration, and proper nutrition.</p>
                      </div>
                      <div className="flex gap-3">
                        <span className="font-semibold text-[#457B9D] flex-shrink-0">Infection:</span>
                        <p>Fever may indicate infection; seek medical attention promptly.</p>
                      </div>
                    </div>
                  </div>
                </details>

                {/* Emotional Side Effects FAQ */}
                <details className="group overflow-hidden rounded-2xl border-2 border-blue-100 bg-gradient-to-br from-white to-blue-50/30 transition-all duration-300 hover:border-blue-200 hover:shadow-md">
                  <summary className="cursor-pointer list-none p-6 font-semibold text-gray-800 transition-colors duration-200 group-open:text-[#457B9D]">
                    <div className="flex items-center justify-between">
                      <span className="text-lg">What are common emotional side effects?</span>
                      <svg
                        className="h-6 w-6 text-[#457B9D] transition-transform duration-300 group-open:rotate-180"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </summary>

                  <div className="border-t border-blue-100 bg-white px-6 pb-6 pt-4">
                    <p className="mb-4 text-gray-700 leading-relaxed">
                      Whether your choice for abortion was met with certainty or coercion,
                      the aftermath can manifest in various emotional challenges:
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {[
                        'Depression',
                        'Sadness',
                        'Anxiety',
                        'Eating disorders',
                        'Substance abuse',
                        'Intimacy issues',
                        'Low self-esteem',
                        'Suicidal thoughts',
                        'Avoidance behaviors',
                        'Fears regarding fertility',
                        'Nightmares or flashbacks'
                      ].map((effect) => (
                        <div key={effect} className="flex items-start gap-2">
                          <svg className="h-5 w-5 text-[#457B9D] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">{effect}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </div>
        </section>

        {/* Support & Resources Section */}
        <section className="mb-16">
          <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-pink-50 to-purple-50 p-8 md:p-12 shadow-lg">
            <div className="mb-6 inline-block rounded-full bg-white/80 px-6 py-2 shadow-sm">
              <span className="text-sm font-semibold uppercase tracking-wide text-[#457B9D]">
                Your Next Steps
              </span>
            </div>

            <h2 className="mb-6 text-3xl font-semibold text-gray-800 md:text-4xl">
              You Have the Power to Choose Healing
            </h2>

            <div className="mb-8 space-y-6 text-lg leading-relaxed text-gray-700">
              <p>
                If you find yourself grappling with any emotional side effects that are
                hindering your daily activities, seeking help is crucial. Many individuals
                have found liberation and healing through our supportive programs. You have
                the power to choose healing, strength, and restoration.
              </p>

              <div className="rounded-2xl bg-white/80 p-6 shadow-md backdrop-blur-sm">
                <p className="mb-4">
                  At She Found His Grace, we're here to walk alongside you on your journey
                  to healing. While we understand the challenges ahead, know that you're not
                  alone.
                </p>
                <p className="font-medium text-gray-800">
                  Take the first step toward healing by reaching out to us today.
                </p>
              </div>
            </div>

            {/* Contact Options */}
            <div className="flex flex-col gap-4 sm:flex-row">
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 font-semibold text-[#457B9D] shadow-md transition-all duration-300 hover:scale-105 hover:shadow-lg"
              >
                <span>Contact Pathway</span>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>

              <a
                href="tel:+15744005272"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white bg-white/50 px-8 py-4 font-semibold text-[#457B9D] backdrop-blur-sm transition-all duration-300 hover:bg-white hover:shadow-lg"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span>Call Helpline: 574-400-5272</span>
              </a>
            </div>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Or visit{" "}
                <a
                  href="http://www.shefoundhisgrace.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-[#457B9D] underline transition-colors hover:text-[#365f7a]"
                >
                  www.shefoundhisgrace.org
                </a>
              </p>
            </div>
          </div>
        </section>

        {/* Link to Pill Reversal Page */}
        <section className="mb-16">
          <div className="overflow-hidden rounded-3xl bg-gradient-to-r from-pink-50 to-pink-100/50 shadow-lg border-2 border-pink-200">
            <div className="p-8 md:p-12 text-center">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-pink-500 px-4 py-2 text-white">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-semibold uppercase tracking-wide">Time-Sensitive</span>
              </div>

              <h2 className="mb-4 text-2xl font-semibold text-gray-800 md:text-3xl">
                Looking for Abortion Pill Reversal?
              </h2>

              <p className="mb-6 text-lg text-gray-700 max-w-2xl mx-auto">
                If you've taken the first dose of the abortion pill and are having regrets,
                there may still be time. Visit our dedicated page for immediate support.
              </p>

              <a
                href="/pill-reversal"
                className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-pink-500 to-pink-600 px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
              >
                <span>Get Help Now</span>
                <svg className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
          </div>
        </section>

      </div>

      {/* Footer Note */}
      <section className="bg-gradient-to-r from-purple-50 via-blue-50 to-pink-50 py-12">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <div className="rounded-2xl bg-white/80 backdrop-blur-sm p-6 shadow-lg">
            <p className="text-lg text-gray-700">
              <span className="font-semibold text-gray-800">Important Note:</span> Pathway does not provide abortions or abortion referrals.
            </p>
          </div>
        </div>
      </section>

      {/* Inline Animation Styles */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
}
